"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import FieldsFormatter from "@/components/organisms/admin/FieldsFormatter";
import { Button } from "@/components/atoms/Button";
import { DeleteDialog } from "@/components/molecules/DeleteDialog";
import { CategoryItem, FormState, VariantConfig } from "../types";
import { ItemRow } from "./ItemRow";

export type OptionalCategoriesFlatUiLabels = {
  listHeading: string;
  addButtonLabel: string;
  emptyMessage: string;
  needsSaveHint: string;
  itemEditTitle: string;
  itemAddTitle: string;
  deleteTitle: string;
  deleteDescription: string;
};

export function mergeOptionalCategoriesFlatUi(
  variantConfig: VariantConfig | null
): OptionalCategoriesFlatUiLabels {
  const o = (variantConfig as any)?.optionalCategoriesFlatUi as
    | Partial<OptionalCategoriesFlatUiLabels>
    | undefined;
  return {
    listHeading: o?.listHeading ?? "Items",
    addButtonLabel: o?.addButtonLabel ?? "Add item",
    emptyMessage: o?.emptyMessage ?? "No items yet. Use the button above to add one.",
    needsSaveHint:
      o?.needsSaveHint ??
      "Click Update Component above to turn off categories on the server, then the list will refresh.",
    itemEditTitle: o?.itemEditTitle ?? "Edit item",
    itemAddTitle: o?.itemAddTitle ?? "Add item",
    deleteTitle: o?.deleteTitle ?? "Delete item",
    deleteDescription:
      o?.deleteDescription ?? "Are you sure you want to delete this item? This action cannot be undone.",
  };
}

export type Form3OptionalCategoriesFlatPanelProps = {
  labels: OptionalCategoriesFlatUiLabels;
  variantConfig: VariantConfig | null;
  reduxState: Record<string, unknown> | null;
  formState: FormState;
  componentFieldsConfig: any[];
  itemFieldsConfig: any[];
  handleComponentFieldsUpdate: (d: Record<string, any>) => void;
  handleComponentSave: () => void | Promise<void>;
  handleItemClick: (category: CategoryItem, item: any) => void;
  handleNewItem: (category: CategoryItem) => void;
  handleItemDelete: (category: CategoryItem, item: any) => void | Promise<void>;
  handleFormFieldsUpdate: (d: Record<string, any>) => void;
  handleCancelForm: () => void;
  handleSave: () => void | Promise<void>;
  pageComponentId?: string;
};

/**
 * Presentational: flat item list when Use categories is off (parent uses `useForm3` once).
 */
export function Form3OptionalCategoriesFlatPanel({
  labels,
  variantConfig,
  reduxState,
  formState,
  componentFieldsConfig,
  itemFieldsConfig,
  handleComponentFieldsUpdate,
  handleComponentSave,
  handleItemClick,
  handleNewItem,
  handleItemDelete,
  handleFormFieldsUpdate,
  handleCancelForm,
  handleSave,
  pageComponentId,
}: Form3OptionalCategoriesFlatPanelProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "item";
    data: any;
    category?: CategoryItem;
  } | null>(null);

  const handleItemDeleteWithConfirm = (category: CategoryItem, item: any) => {
    setDeleteTarget({ type: "item", data: item, category });
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.category) return;
    await handleItemDelete(deleteTarget.category, deleteTarget.data);
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  if (!variantConfig) {
    return null;
  }

  const {
    componentFields,
    componentFieldErrors,
    componentFieldMessages,
    formFields,
    formFieldErrors,
    formFieldMessages,
    categories,
    selectedItemType,
    selectedItemId,
    editingItemId,
    currentFieldsConfig,
    parentId,
  } = formState;

  const titleField =
    variantConfig?.itemTitleFieldName || itemFieldsConfig[0]?.fieldName || "label";
  const descField =
    variantConfig?.itemDescFieldName || itemFieldsConfig[1]?.fieldName || "url";

  const flatBucket =
    categories[0] && (categories[0].id === -1 || categories[0].is_uncategorized_bucket)
      ? categories[0]
      : undefined;
  const flatItems = flatBucket?.items ?? [];
  const flatNeedsSave =
    !flatBucket && categories.some((c) => c.id !== -1 && !c.is_uncategorized_bucket);

  return (
    <>
      {componentFieldsConfig.length > 0 && (
        <div className="mb-6 border-b pb-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <span>📝</span> Component Settings
          </h3>

          <FieldsFormatter
            data={componentFields}
            fields={componentFieldsConfig}
            updateParent={true}
            onChange={handleComponentFieldsUpdate}
            validationErrors={componentFieldErrors}
            validationMessages={componentFieldMessages}
            className="p-0 bg-transparent"
          />

          <div className="mt-4">
            <Button onClick={handleComponentSave} classType="admin">
              {reduxState?.id ? "Update Component" : "Save Component"}
            </Button>
          </div>
        </div>
      )}

      {parentId && pageComponentId && (
        <div className="mb-6 flex-1 overflow-auto">
          {flatNeedsSave && (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {labels.needsSaveHint}
            </div>
          )}
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <span>✨</span> {labels.listHeading}
            </h3>
            <button
              type="button"
              onClick={() => flatBucket && handleNewItem(flatBucket)}
              disabled={!flatBucket}
              className="flex items-center gap-1 text-sm px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:pointer-events-none"
            >
              <Plus className="w-3.5 h-3.5" />
              {labels.addButtonLabel}
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-2 max-h-96 overflow-y-auto">
            {flatItems.length > 0 ? (
              <div className="space-y-2">
                {flatItems.map((item) => {
                  const isItemSelected =
                    selectedItemType === "item" && selectedItemId === item.id;
                  return (
                    <ItemRow
                      key={item.id}
                      item={item}
                      isSelected={isItemSelected}
                      titleField={titleField}
                      descField={descField}
                      onClick={() => flatBucket && handleItemClick(flatBucket, item)}
                      onEdit={() => flatBucket && handleItemClick(flatBucket, item)}
                      onDelete={() => flatBucket && handleItemDeleteWithConfirm(flatBucket, item)}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 text-sm">{labels.emptyMessage}</div>
            )}
          </div>
        </div>
      )}

      {!parentId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <p className="text-blue-800 font-medium mb-2">Component Settings Not Saved Yet</p>
          <p className="text-blue-600 text-sm">
            Please save the Component Settings above before adding items.
          </p>
        </div>
      )}

      {formState.selectedItemType && parentId && pageComponentId && (
        <div className="border-t pt-6">
          <h4 className="font-semibold mb-4">
            {editingItemId ? labels.itemEditTitle : labels.itemAddTitle}
          </h4>

          <FieldsFormatter
            data={formFields}
            fields={currentFieldsConfig}
            updateParent={true}
            onChange={handleFormFieldsUpdate}
            validationErrors={formFieldErrors}
            validationMessages={formFieldMessages}
            className="p-0 bg-transparent"
          />

          <div className="mt-6 flex gap-2">
            <Button onClick={handleSave} classType="admin" className="flex-1">
              {editingItemId ? "Update" : "Save"}
            </Button>
            <Button
              onClick={handleCancelForm}
              classType="admin"
              className="px-4 bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {deleteTarget && (
        <DeleteDialog
          open={isDeleteDialogOpen}
          title={labels.deleteTitle}
          itemName={deleteTarget.data[titleField]}
          description={labels.deleteDescription}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </>
  );
}
